import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DAYS = [
  { value: 0, key: "monday" },
  { value: 1, key: "tuesday" },
  { value: 2, key: "wednesday" },
  { value: 3, key: "thursday" },
  { value: 4, key: "friday" },
  { value: 5, key: "saturday" },
  { value: 6, key: "sunday" },
];

export function DashboardAvailabilityTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    client.get(`/booking/availability/${user.user_id}`, { params: { days_ahead: 1 } }).then((res) => {
      setRules(res.data.weekly_rules || []);
      setDuration(res.data.slot_duration_minutes || 30);
      setLoading(false);
    });
  }, [user]);

  const dayEnabled = (day) => rules.some((r) => r.day_of_week === day);
  const getDayRule = (day) => rules.find((r) => r.day_of_week === day);

  const toggleDay = (day) => {
    setRules((prev) => {
      if (prev.some((r) => r.day_of_week === day)) {
        return prev.filter((r) => r.day_of_week !== day);
      }
      return [...prev, { day_of_week: day, start_time: "10:00", end_time: "16:00" }];
    });
  };

  const updateDayTime = (day, field, value) => {
    setRules((prev) => prev.map((r) => (r.day_of_week === day ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put("/booking/availability", {
        weekly_rules: rules,
        timezone: "Asia/Riyadh",
        slot_duration_minutes: parseInt(duration, 10),
      });
      toast.success(t("booking.availabilitySaved"));
    } catch (e) {
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="rounded-2xl border border-border p-4 sm:p-6 max-w-2xl" data-testid="dashboard-availability-tab">
      <h3 className="font-semibold mb-1">{t("booking.weeklyAvailability")}</h3>
      <p className="text-sm text-muted-foreground mb-5">Asia/Riyadh · {t("booking.timezone")}</p>

      <div className="space-y-3">
        {DAYS.map((d) => {
          const enabled = dayEnabled(d.value);
          const rule = getDayRule(d.value);
          return (
            <div key={d.value} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border p-3">
              <Switch checked={enabled} onCheckedChange={() => toggleDay(d.value)} data-testid={`availability-weekday-toggle-${d.value}`} />
              <span className="min-w-0 flex-1 sm:flex-none sm:w-24 text-sm font-medium">{t(`booking.${d.key}`)}</span>
              {enabled && rule && (
                <div className="flex items-center gap-2 w-full sm:w-auto sm:ms-auto">
                  <Input
                    type="time"
                    value={rule.start_time}
                    onChange={(e) => updateDayTime(d.value, "start_time", e.target.value)}
                    className="min-w-0 flex-1 sm:flex-none sm:w-28 rounded-lg h-9"
                    data-testid={`availability-start-${d.value}`}
                  />
                  <span className="text-muted-foreground text-xs shrink-0">→</span>
                  <Input
                    type="time"
                    value={rule.end_time}
                    onChange={(e) => updateDayTime(d.value, "end_time", e.target.value)}
                    className="min-w-0 flex-1 sm:flex-none sm:w-28 rounded-lg h-9"
                    data-testid={`availability-end-${d.value}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-5">
        <label className="text-sm font-medium">{t("booking.slotDuration")}</label>
        <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-24 rounded-lg h-9" />
      </div>

      <Button
        className="mt-6 rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white"
        onClick={handleSave}
        disabled={saving}
        data-testid="availability-save-button"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("booking.saveAvailability")}
      </Button>
    </Card>
  );
}
